import { dispatchStorageEvent } from "@hooks/useLocalStorage";
import { adminClient, inferAdditionalFields, jwtClient, organizationClient } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";
import { ClientOptions } from "better-auth/types";
import { originForBackend } from "@utils/appOrigin";
import { toast } from "sonner";
import { setActiveOrganizationId } from "./organization-store";

const authOrigin = originForBackend("auth", import.meta.env.VITE_AUTH_URL);
if (!authOrigin) {
	throw new Error("VITE_AUTH_URL is not set");
}

const baseURL = authOrigin + "/auth";

const config = {
	baseURL,
	plugins: [
		inferAdditionalFields({
			user: {
				firstName: {
					type: "string",
					required: false,
					input: true,
				},
				lastName: {
					type: "string",
					required: false,
					input: true,
				},
				acceptedPrivacyPolicyVersion: {
					type: "number",
					required: false,
					input: true,
				},
				acceptedTermsOfServiceVersion: {
					type: "number",
					required: false,
					input: true,
				},
				acceptedNewsletter: {
					type: "boolean",
					required: false,
					input: true,
				},
			},
		}),
		organizationClient(),
		adminClient(),
		jwtClient(),
	],
	fetchOptions: {
		onResponse({ request, response }) {
			const url = request.url.toString().replace(baseURL, "");
			if (url === "/get-session") {
				const jwt = response.headers.get("set-auth-jwt");
				localStorage.removeItem("last-session-check");
				if (!jwt) {
					localStorage.removeItem("better-auth-jwt");
				} else {
					localStorage.setItem("better-auth-jwt", jwt);
				}
				dispatchStorageEvent("better-auth-jwt", jwt);
			}
			if (url === "/sign-out") {
				localStorage.removeItem("better-auth-jwt");
				localStorage.removeItem("last-session-check");
				setActiveOrganizationId(null);
			}
			if (url.startsWith("/sign-in") || url.startsWith("/sign-up")) {
				localStorage.removeItem("better-auth-jwt");
				localStorage.removeItem("last-session-check");
				setActiveOrganizationId(null);
			}
		},
		onError(e) {
			if (e.error.status === 429) {
				toast.error("Too many requests. Please try again later.");
			}
		},
	},
} satisfies ClientOptions;

export const authClient: ReturnType<typeof createAuthClient<typeof config>> = createAuthClient(config);
